import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AuthCheck,
  SendOtp,
  ResetPassword,
  GetUserFeatures,
  GetAllFeatures,
  GetUsers,
  CreateUser,
  UpdateUser,
  ToggleUserStatus,
  GetSubFeatures,
  GetUserOverrides,
  UpdateUserOverrides,
  GetRoles,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const userQueryKeys = {
  userFeatures: () => ["userFeatures"] as const,
  allFeatures: () => ["allFeatures"] as const,
  users: (params?: Parameters<typeof GetUsers>[0]) =>
    ["users", params] as const,
  subFeatures: () => ["subFeatures"] as const,
  userOverrides: (params?: Parameters<typeof GetUserOverrides>[0]) =>
    ["userOverrides", params] as const,
  roles: () => ["roles"] as const,
};

export const invalidateUserQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["userFeatures"] });
  queryClient.invalidateQueries({ queryKey: ["users"] });
  queryClient.invalidateQueries({ queryKey: ["subFeatures"] });
  queryClient.invalidateQueries({ queryKey: ["userOverrides"] });
  queryClient.invalidateQueries({ queryKey: ["roles"] });
};

export const useUserFeaturesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.userFeatures(),
    queryFn: async () => unwrapApiResponse(await GetUserFeatures()),
  });
};

export const useUserFeaturesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: userQueryKeys.userFeatures(),
        queryFn: async () => unwrapApiResponse(await GetUserFeatures()),
      }),
  });
};

export const useAllFeaturesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.allFeatures(),
    queryFn: async () => unwrapApiResponse(await GetAllFeatures()),
  });
};

export const useUsersQuery = (
  params: Parameters<typeof GetUsers>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.users(params),
    queryFn: async () => await GetUsers(params),
  });
};

export const useUsersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetUsers>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: userQueryKeys.users(params),
        queryFn: async () => await GetUsers(params),
      }),
  });
};

export const useSubFeaturesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.subFeatures(),
    queryFn: async () => unwrapApiResponse(await GetSubFeatures()),
  });
};

export const useSubFeaturesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: userQueryKeys.subFeatures(),
        queryFn: async () => unwrapApiResponse(await GetSubFeatures()),
      }),
  });
};

export const useUserOverridesQuery = (
  params: Parameters<typeof GetUserOverrides>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.userOverrides(params),
    queryFn: async () => unwrapApiResponse(await GetUserOverrides(params)),
  });
};

export const useUserOverridesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetUserOverrides>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: userQueryKeys.userOverrides(params),
        queryFn: async () => unwrapApiResponse(await GetUserOverrides(params)),
      }),
  });
};

export const useRolesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: userQueryKeys.roles(),
    queryFn: async () => unwrapApiResponse(await GetRoles()),
  });
};

export const useRolesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: userQueryKeys.roles(),
        queryFn: async () => unwrapApiResponse(await GetRoles()),
      }),
  });
};

export const useAuthCheckMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof AuthCheck>[0]) =>
      AuthCheck(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useSendOtpMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof SendOtp>[0]) => SendOtp(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useResetPasswordMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof ResetPassword>[0]) =>
      ResetPassword(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateUser>[0]) =>
      CreateUser(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateUser>[0]) =>
      UpdateUser(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useToggleUserStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleUserStatus>[0];
      status: Parameters<typeof ToggleUserStatus>[1];
    }) => ToggleUserStatus(payload.id, payload.status),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useUpdateUserOverridesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      userId: Parameters<typeof UpdateUserOverrides>[0];
      overrides: Parameters<typeof UpdateUserOverrides>[1];
    }) => UpdateUserOverrides(payload.userId, payload.overrides),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateUserQueries(queryClient);
      }
    },
  });
};

export const useUserService = (config?: {
  fetchUserFeaturesEnabled?: boolean;
  fetchUsersParams?: Parameters<typeof GetUsers>[0];
  fetchUsersEnabled?: boolean;
  fetchSubFeaturesEnabled?: boolean;
  fetchUserOverridesParams?: Parameters<typeof GetUserOverrides>[0];
  fetchUserOverridesEnabled?: boolean;
  fetchRolesEnabled?: boolean;
}) => {
  const userFeaturesQuery = useUserFeaturesQuery({
    enabled: config?.fetchUserFeaturesEnabled !== false,
  });

  const usersQuery = useUsersQuery(config?.fetchUsersParams, {
    enabled:
      config?.fetchUsersEnabled !== false &&
      config?.fetchUsersParams !== undefined &&
      config?.fetchUsersParams !== null,
  });

  const subFeaturesQuery = useSubFeaturesQuery({
    enabled: config?.fetchSubFeaturesEnabled !== false,
  });

  const userOverridesQuery = useUserOverridesQuery(
    config?.fetchUserOverridesParams,
    {
      enabled:
        config?.fetchUserOverridesEnabled !== false &&
        config?.fetchUserOverridesParams !== undefined &&
        config?.fetchUserOverridesParams !== null,
    },
  );

  const rolesQuery = useRolesQuery({
    enabled: config?.fetchRolesEnabled !== false,
  });

  const authCheckMutation = useAuthCheckMutation();
  const sendOtpMutation = useSendOtpMutation();
  const resetPasswordMutation = useResetPasswordMutation();
  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const toggleUserStatusMutation = useToggleUserStatusMutation();
  const updateUserOverridesMutation = useUpdateUserOverridesMutation();

  return {
    userFeaturesQuery,
    usersQuery,
    subFeaturesQuery,
    userOverridesQuery,
    rolesQuery,
    authCheckMutation,
    sendOtpMutation,
    resetPasswordMutation,
    createUserMutation,
    updateUserMutation,
    toggleUserStatusMutation,
    updateUserOverridesMutation,
  };
};
